import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-pre-admission',
   imports: [CommonModule, RouterModule],
  templateUrl: './pre-admission.component.html',
  styleUrl: './pre-admission.component.css',
})
export class PreAdmissionComponent {
    /* ── Form model ── */
  form = {
    parentName: '',
    email: '',
    phone: '',
    yearGroup: ''
  };
 
  formSubmitted = false;
  formError = false;
 
  /* ── FAQ state ── */
  faqs = [
    {
      q: 'Is the assessment really free?',
      a: 'Yes — completely free, with no payment required at any stage. Every registered child receives the full RAR and AI-driven feedback report plus a webinar invite at zero cost to you.',
      open: true
    },
    {
      q: 'Which year groups can take part?',
      a: 'The assessment is designed for children currently in Year 3, Year 4, or Year 5. Each cohort receives a year-group-specific paper calibrated to the appropriate curriculum level and 11+ readiness benchmark.',
      open: false
    },
    {
      q: 'What does the RAR dashboard actually show?',
      a: 'The RAR dashboard displays your child\'s accuracy indices broken down by question type and sub-topic, sectional pacing data showing where time was lost, macro strength summaries, and prioritised next-step remedial recommendations — all generated by Sehyog\'s proprietary AI engine.',
      open: false
    },
    {
      q: 'When and how does my child sit the assessment?',
      a: 'After registration you will receive a secure access link by email. Your child sits the timed assessment online at home on any device — laptop, tablet, or desktop. No additional software is needed.',
      open: false
    },
    {
      q: 'Why are places limited?',
      a: 'Unlike bulk-marking systems, Sehyog\'s diagnostic engine processes each child\'s paper individually — analysing accuracy at sub-question level and generating a bespoke report. This level of computational depth means we must cap each cohort to maintain the quality and precision of every output.',
      open: false
    }
  ];
 
  /* ── Static data ── */
  pillars = [
    {
      num: '01',
      icon: '🎯',
      cls: 'al-p1',
      title: 'Stop Guessing, Start Measuring',
      body: 'Our assessments go far beyond simple pass/fail marks. We analyse your child\'s accuracy indices down to specific multi-step question subsets — so you see exactly which question types are costing marks, not just which topics.',
      tag: 'Accuracy Analysis'
    },
    {
      num: '02',
      icon: '⏱️',
      cls: 'al-p2',
      title: 'The Stamina Check',
      body: 'The biggest barrier to exam success is often the clock — not knowledge. We measure timed sectional pacing under strict parameters to pinpoint exactly where speed breaks down, section by section and question by question.',
      tag: 'Timed Pacing Data'
    },
    {
      num: '03',
      icon: '🤖',
      cls: 'al-p3',
      title: 'The AI-Powered Analytics Edge',
      body: 'Access a personalised feedback dashboard — built on Sehyog\'s proprietary RAR engine — detailing your child\'s macro strengths, micro error patterns, and immediate next-step remedial recommendations unique to their data.',
      tag: 'RAR + AI Dashboard'
    }
  ];
 
  whyCards = [
    { icon: '🎯', cls: 'al-ic1', title: 'See Your Child\'s True Level', body: 'Get a raw score benchmarked against thousands of pupils sitting the same paper across the UK — not just your child\'s class.' },
    { icon: '🔦', cls: 'al-ic2', title: 'Highlight Strengths & Areas to Grow', body: 'Our diagnostic pinpoints the precise topics your child excels in and the gaps that need addressing before the next academic year.' },
    { icon: '🚀', cls: 'al-ic3', title: 'Build Confidence & Motivation', body: 'Children who understand where they stand perform better. Clear feedback turns anxiety into a structured, achievable plan.' },
    { icon: '🗺️', cls: 'al-ic4', title: 'Plan the Right Learning Path', body: 'Receive a personalised roadmap so every revision session is targeted, efficient, and calibrated to your child\'s actual needs.' }
  ];
 
  structureCards = [
    {
      num: '1',
      cls: 'al-sc-cyan',
      title: 'National Curriculum Assessment',
      mark: '100 marks — covering key UK curriculum areas',
      topics: ['Number & Place Value', 'Measurement', 'Addition & Subtraction', 'Statistics', 'Multiplication & Division', 'Word Problems', 'Fractions', 'Geometry']
    },
    {
      num: '2',
      cls: 'al-sc-gold',
      title: 'Mathematical Thinking Challenge',
      mark: '50 marks — testing reasoning and problem solving',
      topics: ['Logical Reasoning', 'Mathematical Thinking', 'Pattern Recognition', 'Analytical Skills', 'Problem Solving', 'Multi-Step Questions']
    }
  ];
 
  receivesCards = [
    { icon: '📄', title: 'Detailed Feedback Report', body: 'A full PDF showing your child\'s marks, percentile rank, accuracy indices, and performance commentary section by section.' },
    { icon: '🔍', title: 'Topic-by-Topic Analysis', body: 'Exact scores for every sub-topic so you know precisely what\'s strong and what needs targeted attention before September.' },
    { icon: '⚡', title: 'Strengths & Areas for Improvement', body: 'A visual summary of where your child excels and the specific areas most likely to cost marks in an 11+ exam setting.' },
    { icon: '🗺️', title: 'Suggested Learning Plans', body: 'Personalised study priorities and resource recommendations calibrated to your child\'s individual diagnostic results.' },
    { icon: '📋', title: 'Tailored Practice Sheets', body: 'Ready-to-use worksheets mapped directly to your child\'s weakest sub-topics — print and begin immediately.' },
    { icon: '🎥', title: 'Expert-Led Parent Webinar', body: 'A live session with Sehyog\'s senior tutors to decode your results together, ask questions, and build a clear next-steps plan.' }
  ];
 
  howSteps = [
    { num: '1', cls: 'al-s1', title: 'Register in 90 Seconds', body: 'Fill in the form above with your name, email, phone number, and your child\'s year group. Instant confirmation is sent to your inbox.' },
    { num: '2', cls: 'al-s2', title: 'Sit the Assessment Online', body: 'Your child completes the timed assessment from home on any device. The paper is structured around the Year 3–5 national curriculum and 11+ core skill sets.' },
    { num: '3', cls: 'al-s3', title: 'Receive Your RAR + AI Report', body: 'Within 48 hours, your personalised feedback dashboard goes live — accuracy indices, stamina pacing data, macro strengths, and prioritised remedial actions.' },
    { num: '4', cls: 'al-s4', title: 'Join the Expert Results Webinar', body: 'Attend a live session with Sehyog\'s senior tutors to decode your child\'s report, understand the data, and leave with a clear action plan.' }
  ];
 
  webinarChecklist = [
    'How to read your child\'s RAR dashboard',
    'The most common stamina breakdown points',
    'Topic-by-topic remedial priorities explained',
    'Recommended next steps for each year group',
    'Live Q&A with Sehyog expert tutors'
  ];
 
  seatStatus = [
    { label: 'Year 3 cohort:', status: 'Filling fast' },
    { label: 'Year 4 cohort:', status: 'Filling fast' },
    { label: 'Year 5 cohort:', status: 'Limited seats left' }
  ];
 
  ngOnInit() {
    /* Load Nunito font dynamically — Angular 7 pattern */
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);
  }
 
  toggleFaq(index: number) {
    var self = this;
    self.faqs.forEach(function(faq, i) {
      if (i === index) {
        faq.open = !faq.open;
      } else {
        faq.open = false;
      }
    });
  }
 
  scrollToRegister() {
    var el = document.getElementById('al-register');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
 
  scrollToHowItWorks() {
    var el = document.getElementById('al-how-it-works');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
 
  onSubmit() {
    var self = this;
    if (!self.form.parentName || !self.form.email || !self.form.phone || !self.form.yearGroup) {
      self.formError = true;
      return;
    }
    self.formError = false;
    self.formSubmitted = true;
    /* TODO: wire to your API / ClassService here */
    console.log('Assessment registration:', self.form);
  }

}
